import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Timer, Target } from "@phosphor-icons/react";

export default function SkillChallenge({ challenge, onSubmit, submitting }) {
  const [pos, setPos] = useState(0);
  const [stopped, setStopped] = useState(false);
  const posRef = useRef(0);
  const dirRef = useRef(1);
  const rafRef = useRef(null);
  const lastRef = useRef(null);

  const speed = challenge.speed || 55; // units per second

  useEffect(() => {
    const tick = (t) => {
      if (lastRef.current == null) lastRef.current = t;
      const dt = (t - lastRef.current) / 1000;
      lastRef.current = t;
      let next = posRef.current + dirRef.current * speed * dt;
      if (next >= 100) {
        next = 100;
        dirRef.current = -1;
      } else if (next <= 0) {
        next = 0;
        dirRef.current = 1;
      }
      posRef.current = next;
      setPos(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stop = () => {
    if (stopped || submitting) return;
    cancelAnimationFrame(rafRef.current);
    setStopped(true);
    onSubmit(Number(posRef.current.toFixed(2)));
  };

  const zoneLeft = challenge.zone_start;
  const zoneWidth = challenge.zone_end - challenge.zone_start;

  return (
    <div className="mx-auto max-w-2xl">
      <div className="ff-panel rounded-md p-6 sm:p-8">
        <div className="mb-2 flex items-center gap-2 text-ff-gold">
          <Timer size={22} weight="duotone" />
          <span className="font-mono-ff text-xs uppercase tracking-widest">Skill Challenge · The Crucial Moment</span>
        </div>
        <h2 className="font-display text-3xl font-bold tracking-tight text-ff-parchment">Steady Your Hand</h2>
        <p className="mt-2 text-sm text-ff-muted">
          Stop the marker inside the golden zone. A center hit gives the greatest bonus to your expedition.
        </p>

        <div className="relative mt-8 h-16">
          {/* bar */}
          <div className="absolute inset-x-0 top-1/2 h-4 -translate-y-1/2 overflow-hidden rounded-full border border-ff-brass/30 bg-ff-bg">
            {/* target zone */}
            <div
              className="animate-pulse-gold absolute top-0 h-full bg-ff-gold/70"
              style={{ left: `${zoneLeft}%`, width: `${zoneWidth}%` }}
            >
              <div className="absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2 bg-ff-bg/70" />
            </div>
          </div>
          {/* marker */}
          <div
            className="absolute top-1/2 h-10 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ff-parchment shadow-[0_0_10px_rgba(242,232,211,0.8)]"
            style={{ left: `${pos}%` }}
            data-testid="challenge-marker"
          >
            <Target size={16} className="absolute -top-6 left-1/2 -translate-x-1/2 text-ff-parchment" />
          </div>
        </div>

        <div className="mt-2 flex justify-between font-mono-ff text-[10px] text-ff-muted">
          <span>0</span>
          <span>Marker: {pos.toFixed(0)}</span>
          <span>100</span>
        </div>

        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={stop}
          disabled={stopped || submitting}
          data-testid="challenge-stop-btn"
          className="mt-8 w-full rounded-sm bg-ff-gold py-4 text-lg font-bold text-ff-bg transition-colors hover:bg-ff-brass disabled:opacity-60"
        >
          {stopped ? "..." : "STOP"}
        </motion.button>
      </div>
    </div>
  );
}
